
import { useEffect, useRef, useState } from "react";
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  useSpring,
  useTransform,
} from "framer-motion";
import { cn } from "@/lib/utils";

// ============================================================================
// AnimatedNumber — spring-driven number primitive
// ============================================================================
//
// Renders its initial value instantly (no 0→value count-up on page load) and
// springs between subsequent values. Respects prefers-reduced-motion by
// snapping instead of animating. Applies tabular-nums so digits don't jitter
// while rolling.

/** Structural subset of framer-motion's SpringOptions (not re-exported publicly). */
export interface AnimatedNumberSpringConfig {
  stiffness?: number;
  damping?: number;
  mass?: number;
  restDelta?: number;
  restSpeed?: number;
}

const DEFAULT_SPRING: AnimatedNumberSpringConfig = {
  stiffness: 120,
  damping: 20,
};

/** Default: whole number with locale separators (1,234). */
const defaultFormat = (n: number) => Math.round(n).toLocaleString();

export interface AnimatedNumberProps {
  value: number;
  /**
   * Formatter applied every animation frame. Receives fractional in-between
   * values — round inside your formatter if you need whole numbers
   * (e.g. `(n) => formatNumber(Math.round(n))` for compact "1.0K" output).
   */
  format?: (n: number) => string;
  className?: string;
  /** Spring override; read once on mount. */
  springConfig?: AnimatedNumberSpringConfig;
}

export function AnimatedNumber({
  value,
  format = defaultFormat,
  className,
  springConfig,
}: AnimatedNumberProps) {
  const prefersReducedMotion = useReducedMotion();
  // useSpring initialises AT `value`, so the mount render shows the real
  // number immediately — only later prop changes animate.
  const spring = useSpring(value, springConfig ?? DEFAULT_SPRING);
  const display = useTransform(spring, (v) => format(v));

  useEffect(() => {
    if (prefersReducedMotion) {
      spring.jump(value); // snap, no spring
    } else {
      spring.set(value);
    }
  }, [value, prefersReducedMotion, spring]);

  return (
    <motion.span
      className={cn("tabular-nums", className)}
      style={{ fontVariantNumeric: "tabular-nums" }}
    >
      {display}
    </motion.span>
  );
}

// ============================================================================
// AnimatedDelta — AnimatedNumber + gold/oxblood flash + floating ± chip
// ============================================================================
//
// Wraps AnimatedNumber. When `value` increases: the number briefly flashes
// gold and a `+N` chip floats up and fades. When it decreases: oxblood flash
// and a `−N` chip. Everything (flash, chip, spring) is gated behind
// prefers-reduced-motion.

const FLASH_MS = 600;
const CHIP_DURATION_S = 0.6;
const CHIP_RISE_PX = -24;
const MAX_CHIPS = 3;

const DOWN_CLASS = "text-oxblood-text";
// `brightness-125` makes the flash register even on text that is already gold.
const UP_CLASS = "text-gold brightness-125";

let nextChipId = 0;

interface DeltaChip {
  id: number;
  delta: number;
}

export interface AnimatedDeltaProps extends AnimatedNumberProps {
  /** Formatter for the floating ± chip; defaults to `format`. */
  formatDelta?: (n: number) => string;
  /** Extra classes for the floating chip (e.g. text size in tight navs). */
  chipClassName?: string;
}

export function AnimatedDelta({
  value,
  format = defaultFormat,
  formatDelta,
  className,
  chipClassName,
  springConfig,
}: AnimatedDeltaProps) {
  const prefersReducedMotion = useReducedMotion();
  const prevValue = useRef(value);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [flash, setFlash] = useState<"up" | "down" | null>(null);
  const [chips, setChips] = useState<DeltaChip[]>([]);

  const chipFormat = formatDelta ?? format;

  useEffect(() => {
    const delta = value - prevValue.current;
    prevValue.current = value;
    if (delta === 0 || prefersReducedMotion) return;

    setFlash(delta > 0 ? "up" : "down");
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(null), FLASH_MS);

    setChips((current) => [
      ...current.slice(-(MAX_CHIPS - 1)),
      { id: nextChipId++, delta },
    ]);
  }, [value, prefersReducedMotion]);

  // Clear the pending flash timer on unmount.
  useEffect(() => {
    return () => {
      if (flashTimer.current) clearTimeout(flashTimer.current);
    };
  }, []);

  return (
    <span className="relative inline-flex">
      <AnimatedNumber
        value={value}
        format={format}
        springConfig={springConfig}
        className={cn(
          "transition-[color,filter] duration-300 ease-out",
          className,
          flash === "up" && UP_CLASS,
          flash === "down" && DOWN_CLASS,
        )}
      />
      {/* Floating ± chips — absolutely positioned over the number so they
          never affect layout; ancestors must not clip (no overflow-hidden). */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 flex items-center justify-center"
      >
        <AnimatePresence>
          {chips.map((chip) => (
            <motion.span
              key={chip.id}
              initial={{ y: 0, opacity: 1 }}
              animate={{ y: CHIP_RISE_PX, opacity: 0 }}
              transition={{ duration: CHIP_DURATION_S, ease: "easeOut" }}
              onAnimationComplete={() =>
                setChips((current) => current.filter((c) => c.id !== chip.id))
              }
              className={cn(
                "absolute whitespace-nowrap font-serif text-xs font-bold tabular-nums",
                chip.delta > 0 ? "text-gold" : DOWN_CLASS,
                chipClassName,
              )}
            >
              {chip.delta > 0
                ? `+${chipFormat(chip.delta)}`
                : `−${chipFormat(-chip.delta)}`}
            </motion.span>
          ))}
        </AnimatePresence>
      </span>
    </span>
  );
}
